import React, { useState, useEffect } from 'react';
import NavigationBar from './NavigationBar';
import HamburgerMenu from './HamburgerMenu';
import Footer from './Footer';

function AboutPage({ navigateTo, goBack, userProfile, isLoggedIn }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Detect mobile/desktop
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      padding: isMobile ? '20px 16px 60px' : '40px 20px 80px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {!isMobile && <NavigationBar currentPage="about" isLoggedIn={isLoggedIn} navigateTo={navigateTo} userProfile={userProfile} />}
      {isMobile && <HamburgerMenu currentPage="about" isLoggedIn={isLoggedIn} navigateTo={navigateTo} userProfile={userProfile} />}
      
      <div style={{
        maxWidth: '720px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '32px',
          marginTop: isMobile ? '50px' : '20px',
          color: 'white'
        }}>
          <h1 style={{
            fontSize: isMobile ? '28px' : '42px',
            fontWeight: isMobile ? '700' : '800',
            margin: '0 0 12px 0',
            letterSpacing: '-0.5px'
          }}>
            About SudokuMatch
          </h1>
          <p style={{
            fontSize: '16px',
            opacity: 0.9,
            margin: '0',
            fontWeight: '500'
          }}>
            Built for players who care about the puzzle.
          </p>
        </div>

        {/* Section Cards */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          {/* Section: Why This Exists */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '28px 24px' : '32px 40px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            lineHeight: '1.7'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#1F2937',
              margin: '0 0 20px 0',
              letterSpacing: '-0.3px'
            }}>
              Why This Exists
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0 0 16px 0'
            }}>
              Sudoku has been part of daily routines for millions of people for decades.
            </p>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0 0 16px 0'
            }}>
              It's a game of logic, focus, and quiet satisfaction — and for many players, the experience hasn't changed much over time.
            </p>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0'
            }}>
              SudokuMatch exists to refine that experience. Not to replace it, overcomplicate it, or turn it into something it isn't — but to make it smoother, fairer, and more rewarding for modern players.
            </p>
          </div>

          {/* Section: The Player Comes First */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '28px 24px' : '32px 40px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            lineHeight: '1.7'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#1F2937',
              margin: '0 0 20px 0',
              letterSpacing: '-0.3px'
            }}>
              The Player Comes First
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0 0 16px 0'
            }}>
              Everything on SudokuMatch is designed around one principle: the puzzle comes first.
            </p>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0 0 16px 0'
            }}>
              The grid should be responsive. The controls should feel natural. Nothing should get in the way of simply solving.
            </p>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0'
            }}>
              Whether you're playing for a few quiet minutes or pushing yourself to improve, the experience should feel seamless and familiar — not distracting or gimmicky.
            </p>
          </div>

          {/* Section: Skill, Not Shortcuts */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '28px 24px' : '32px 40px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            lineHeight: '1.7'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#1F2937',
              margin: '0 0 20px 0',
              letterSpacing: '-0.3px'
            }}>
              Skill, Not Shortcuts
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0 0 16px 0'
            }}>
              For players who want it, SudokuMatch also offers a way to understand — and demonstrate — skill.
            </p>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0 0 16px 0'
            }}>
              That means fair conditions, clear rules, and no advantages for rushing or exploiting the system.
            </p>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0'
            }}>
              Competition is always optional. But when it exists, it should feel earned.
            </p>
          </div>

          {/* Section: Finding Out How Good You Are */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '28px 24px' : '32px 40px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            lineHeight: '1.7'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#1F2937',
              margin: '0 0 20px 0',
              letterSpacing: '-0.3px'
            }}>
              Finding Out How Good You Are
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0 0 16px 0'
            }}>
              Many Sudoku players quietly wonder the same thing: How good am I, actually?
            </p>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0 0 16px 0'
            }}>
              SudokuMatch aims to provide a space where players can track their performance over time, compare fairly against others solving the same puzzle, and choose whether they simply want to play — or prove something to themselves.
            </p>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0'
            }}>
              No pressure. No obligation. Just the option.
            </p>
          </div>

          {/* Section: Built Independently */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '28px 24px' : '32px 40px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            lineHeight: '1.7'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#1F2937',
              margin: '0 0 20px 0',
              letterSpacing: '-0.3px'
            }}>
              Built Independently
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0 0 16px 0'
            }}>
              SudokuMatch is being built independently by an avid Sudoku enthusiast.
            </p>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0'
            }}>
              There's no large studio behind it, no push to maximise attention at the cost of experience. The goal is simple: to build the kind of Sudoku platform the creator always wished existed.
            </p>
          </div>

          {/* Section: Looking Ahead */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '28px 24px' : '32px 40px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            lineHeight: '1.7'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#1F2937',
              margin: '0 0 20px 0',
              letterSpacing: '-0.3px'
            }}>
              Looking Ahead
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0 0 16px 0'
            }}>
              This is just the beginning.
            </p>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0'
            }}>
              The platform will continue to evolve carefully — guided by player feedback, thoughtful experimentation, and a commitment to keeping Sudoku enjoyable above all else.
            </p>
          </div>

          {/* Founder Link Section */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '28px 24px' : '32px 40px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            lineHeight: '1.7'
          }}>
            <p style={{
              fontSize: '16px',
              color: '#4B5563',
              margin: '0 0 20px 0'
            }}>
              If you're curious about how SudokuMatch plans to grow — and how early players can help shape it — more information is available through the Founder programme.
            </p>
            <button
              onClick={() => navigateTo('founder')}
              style={{
                background: 'none',
                border: 'none',
                color: '#FF8C00',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                padding: '0',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.target.style.textDecoration = 'none';
              }}
            >
              Learn more about becoming a Founder
              <span style={{ fontSize: '14px' }}>→</span>
            </button>
          </div>

          {/* Privacy Policy Link Section */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '28px 24px' : '32px 40px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            lineHeight: '1.7'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#1F2937',
              margin: '0 0 20px 0',
              letterSpacing: '-0.3px'
            }}>
              Privacy Policy
            </h2>
            <button
              onClick={() => navigateTo('privacy')}
              style={{
                background: 'none',
                border: 'none',
                color: '#FF8C00',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                padding: '0',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.target.style.textDecoration = 'none';
              }}
            >
              View our privacy policy
              <span style={{ fontSize: '14px' }}>→</span>
            </button>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '40px' }}>
        <Footer navigateTo={navigateTo} />
      </div>
    </div>
  );
}

export default AboutPage;
